import React, { RefObject } from 'react'
import { format } from 'date-fns'

import {
  Button,
  Header,
  MoreButton,
  StatusPopup,
  Text,
  textChain,
  useStatusPopup,
} from '@revolut-internal/ui-kit'

import {
  useAlertingIncidentsTimelinesMutation,
  useCreateAlertingPolicy,
  useDeleteAlertingPolicy,
} from 'modules/api/alerting/queries'
import { Security } from 'modules/security/security'
import { useToasts } from 'modules/toasts/ToastsProvider'
import {
  AlertingEntityType,
  AlertingPolicyCreateRequest,
  AlertingPolicyGetResponse,
  SpecialisationDataWithoutSystem,
} from 'types'

import { QueueHealthPolicyForm } from './QueueHealthPolicyForm'
import { QueueHealthPolicyProvider } from './QueueHealthPolicyProvider'
import { QueueHealthPolicySkeleton } from './QueueHealthPolicySkeleton'
import { AlertingPolicyForm } from './types'
import { convertFormToPolicy } from './utils'

export type QueueHealthPolicyProps = {
  specialisation?: SpecialisationDataWithoutSystem
  policy?: AlertingPolicyGetResponse | null
  scrollRef?: RefObject<HTMLDivElement | null>
  isPolicyPending?: boolean
  onBack: VoidFunction
}

export const QueueHealthPolicy = ({
  specialisation,
  policy,
  scrollRef,
  isPolicyPending,
  onBack,
}: QueueHealthPolicyProps) => {
  const { addSuccessToast } = useToasts()
  const policyDeletePopup = useStatusPopup()

  const isReadonly = !Security.canCreatePolicy
  const { mutateAsync: getIncidents, isPending: isIncidentsPending } =
    useAlertingIncidentsTimelinesMutation({
      // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
      entityId: specialisation?.id!,
      entityType: AlertingEntityType.Specialisation,
    })
  const { mutateAsync: savePolicy, isPending: isPolicySaving } = useCreateAlertingPolicy()
  const { mutateAsync: deletePolicy } = useDeleteAlertingPolicy()

  const handleUpdatePolicyConfirm = async (newPolicy: AlertingPolicyCreateRequest) => {
    await savePolicy(newPolicy)

    policyDeletePopup.hide()
    onBack()
    addSuccessToast('Policy updated and incident closed')
  }

  const handleSavePolicy = async (formValues: AlertingPolicyForm) => {
    if (!specialisation) {
      return
    }

    const newPolicy = convertFormToPolicy({
      ...formValues,
      entityId: specialisation.id,
      entityType: AlertingEntityType.Specialisation,
    })

    if (policy) {
      const incidents = await getIncidents()

      if (incidents.items.some(incident => !incident.resolvedDate)) {
        policyDeletePopup.show(
          <StatusPopup variant="warning" onClose={policyDeletePopup.hide}>
            <StatusPopup.Title>
              Modify policy and resolve ongoing incidents?
            </StatusPopup.Title>
            <StatusPopup.Description>
              Modifying the policy will automatically resolve any ongoing incidents
            </StatusPopup.Description>
            <StatusPopup.Actions>
              <Button elevated onClick={() => handleUpdatePolicyConfirm(newPolicy)}>
                Confirm
              </Button>
              <Button variant="secondary" elevated onClick={policyDeletePopup.hide}>
                Cancel
              </Button>
            </StatusPopup.Actions>
          </StatusPopup>,
        )

        return
      }
    }

    await savePolicy(newPolicy)

    addSuccessToast(`Policy ${policy ? 'updated' : 'created'}`)
    onBack()
  }

  const handleDeletePolicy = async () => {
    if (!policy) {
      return
    }

    await deletePolicy(policy.alertingPolicyId)

    policyDeletePopup.hide()
    onBack()
    addSuccessToast('Policy deleted')
  }

  const handleDeletePopupOpen = () => {
    policyDeletePopup.show(
      <StatusPopup variant="warning" onClose={policyDeletePopup.hide}>
        <StatusPopup.Title>
          Remove policy and resolve ongoing incidents?
        </StatusPopup.Title>
        <StatusPopup.Description>
          This will delete the policy and automatically resolve any ongoing incidents
        </StatusPopup.Description>
        <StatusPopup.Actions>
          <Button elevated onClick={handleDeletePolicy}>
            Confirm
          </Button>
          <Button variant="secondary" elevated onClick={policyDeletePopup.hide}>
            Cancel
          </Button>
        </StatusPopup.Actions>
      </StatusPopup>,
    )
  }

  if (isPolicyPending) {
    return <QueueHealthPolicySkeleton onBack={onBack} />
  }

  if (!specialisation) {
    return null
  }

  return (
    <>
      <Header>
        <Header.BackButton aria-label="Close" onClick={onBack} />
        <Header.Title>Policy for {specialisation.name}</Header.Title>

        {policy && (
          <Header.Subtitle>
            <Text variant="body2" color="grey-tone-50">
              {textChain(
                `Modified by ${policy.updatedBy}`,
                format(policy.updatedDate, 'd MMM yyyy, HH:mm'),
              )}
            </Text>
          </Header.Subtitle>
        )}

        {!isReadonly && Security.canDeletePolicy && policy && (
          <Header.Actions>
            <MoreButton variant="icon">
              <MoreButton.Action
                useIcon="Delete"
                variant="negative"
                onClick={handleDeletePopupOpen}
              >
                Delete policy
              </MoreButton.Action>
            </MoreButton>
          </Header.Actions>
        )}
      </Header>

      <QueueHealthPolicyProvider specialisation={specialisation} scrollRef={scrollRef}>
        <QueueHealthPolicyForm
          policy={policy}
          isPending={isPolicySaving || isIncidentsPending}
          onSubmit={handleSavePolicy}
          onTriggerThresholdDeletion={handleDeletePopupOpen}
        />
      </QueueHealthPolicyProvider>
    </>
  )
}
